<template>
    <v-sheet v-if="isReady" class="video-player" :elevation="elevation" :width="width">
        <v-responsive :aspect-ratio="16 / 9">
            <video ref="video" class="video-player__video video-js vjs-fluid">
                <track
                    v-for="(crtTrack, crtIndex) in tracks"
                    :key="crtIndex"
                    class="video-player__track"
                    :kind="crtTrack.kind"
                    :label="crtTrack.label"
                    :src="crtTrack.src"
                    :srcLang="crtTrack.srcLang"
                    :default="crtTrack.default"
                />
            </video>
        </v-responsive>
    </v-sheet>
</template>

<script>
import videojs from 'video.js';
import 'videojs-youtube';

const DEFAULT_EVENTS = [
    'loadeddata',
    'canplay',
    'canplaythrough',
    'play',
    'pause',
    'waiting',
    'playing',
    'ended',
    'error'
];

export default {
    name: 'AppVideo',
    props: {
        crossOrigin: {
            type: String,
            default: ''
        },
        playsinline: {
            type: Boolean,
            default: false
        },
        options: {
            type: Object,
            default: () => ({})
        },
        globalOptions: {
            type: Object,
            default: () => ({
                // autoplay: false,
                controls: true,
                // preload: 'auto',
                // fluid: false,
                // muted: false,
                controlBar: {
                    remainingTimeDisplay: false,
                    playToggle: {},
                    progressControl: {},
                    fullscreenToggle: {},
                    volumeMenuButton: {
                        inline: false,
                        vertical: true
                    }
                },
                techOrder: ['html5'],
                plugins: {}
            })
        },
        events: {
            type: Array,
            default: () => []
        },
        trackList: {
            type: Array,
            default: () => []
        },
        elevation: {
            type: [Number, String],
            default: 6
        },
        width: {
            type: [Number, String],
            default: null
        }
    },
    data: () => ({
        player: null,
        isReady: true
    }),
    computed: {
        tracks() {
            return this.trackList.map(track => {
                return {
                    kind: track.kind,
                    label: track.label,
                    src: track.src,
                    srcLang: track.srcLang,
                    default: track.default
                };
            });
        }
    },
    watch: {
        options: {
            deep: true,
            handler(options) {
                if (options) {
                    this.dispose(() => {
                        if (options && options.sources && options.sources.length) {
                            this.initialisePlayer();
                        }
                    });
                }
            }
        }
    },
    beforeDestroy() {
        if (this.player) {
            this.dispose();
        }
    },
    methods: {
        setup() {
            if (!this.player) {
                this.initialisePlayer();
            }
        },
        emitPlayerState(event, value) {
            if (event) {
                this.$emit(event, this.player);
            }
            if (value) {
                this.$emit('statechanged', { [event]: value });
            }
        },
        initialisePlayer() {
            let options = {
                ...this.globalOptions
            };

            if (this.options) {
                options = {
                    ...options,
                    ...this.options
                };
            } else {
                return;
            }

            if (this.playsinline) {
                this.$refs.video.setAttribute('playsinline', this.playsinline);
                this.$refs.video.setAttribute('webkit-playsinline', this.playsinline);
                this.$refs.video.setAttribute('x5-playsinline', this.playsinline);
                this.$refs.video.setAttribute('x5-video-player-type', 'h5');
                this.$refs.video.setAttribute('x5-video-player-fullscreen', false);
            }

            if (this.crossOrigin) {
                this.$refs.video.crossOrigin = this.crossOrigin;
                this.$refs.video.setAttribute('crossOrigin', this.crossOrigin);
            }

            if (options.plugins) {
                delete options.plugins.__ob__;
            }

            if (options.poster) {
                this.$refs.video.setAttribute('poster', options.poster);
            }

            this.player = videojs(this.$refs.video, options, () => {
                const events = {
                    ...DEFAULT_EVENTS,
                    ...this.events
                };
                this._.each(events, event => {
                    this.$on(event, () => {
                        this.emitPlayerState(event, true);
                    });
                });
            });

            this.$on('timeupdate', () => {
                this.emitPlayerState('timeupdate', this.currentTime());
            });

            this.$emit('ready', this);
        },
        dispose(callback) {
            try {
                if (this.player && this.player.dispose) {
                    if (this.player.techName_ !== 'Flash') {
                        this.player.pause && this.player.pause();
                    }

                    this.player.dispose();
                    this.player = null;
                }
            } catch (error) {
                //
            }

            this.$nextTick(() => {
                this.isReady = false;
                this.$nextTick(() => {
                    this.isReady = true;
                    this.$nextTick(() => {
                        callback && callback();
                    });
                });
            });
        }
    }
};
</script>

<style lang="scss">
@import 'video.js/dist/video-js.min.css';
.video-player {
    position: relative;
    min-width: 20rem;
    &__video {
        position: relative;
        max-width: 100%;
        video {
            background: #000;
        }
        .vjs-big-play-button {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translateX(-50%) translateY(-50%);
            font-size: 2rem;
            height: 3.2rem;
            background: var(--v-primaryText-base);
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 4px;
            .vjs-icon-placeholder:before {
                color: rgba(255, 255, 255, 0.7);
                position: absolute;
                top: 50%;
                left: 50%;
                width: auto;
                height: auto;
                transform: translate(-50%, -50%);
                font-size: 2.6rem;
                transition: all 0.15s ease;
            }
        }
        .vjs-big-play-button:focus,
        &:hover .vjs-big-play-button {
            background: var(--v-primaryText-base);
            .vjs-icon-placeholder:before {
                color: rgba(255, 255, 255, 1);
            }
        }
        .vjs-control-bar {
            background: var(--v-primaryText-base);
        }
        .vjs-slider {
            background: rgba(0, 0, 0, 0.1);
        }
        .vjs-load-progress {
            background: rgba(0, 0, 0, 0.6);
            div {
                background: rgba(0, 0, 0, 0.6);
            }
        }
        .vjs-time-tooltip {
            padding: 0.35rem 0.5rem 0.3rem;
            color: var(--v-primaryText-base);
            background: rgba(255, 255, 255, 0.9);
            font-weight: bold;
        }
        .vjs-mouse-display {
            background: rgba(255, 255, 255, 0.8);
        }
        .vjs-progress-control:hover .vjs-progress-holder:focus .vjs-time-tooltip,
        .vjs-progress-control:hover .vjs-time-tooltip {
            font-size: 0.8rem;
        }
    }
}
</style>
